import { type CSSObject } from '@emotion/react';
import { type StyledComponent } from '@emotion/styled';
import { alpha } from '@mui/material/styles';
import { boxClasses } from '@mui/material/Box';
import { inputBaseClasses } from '@mui/material/InputBase';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import MuiSelect from '@mui/material/Select';
import { selectClasses } from '@mui/material/Select';
import { type SelectProps as MuiSelectProps } from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import type { DetailedHTMLProps, HTMLAttributes, ReactNode } from 'react';

import { type Theme } from '../../../../theme';

type SelectProps = {
  hasError?: boolean;
  isFilled?: boolean;
  theme: Theme;
  startAdornment?: ReactNode;
  fullWidth?: boolean;
};

type StyledSelectProps = StyledComponent<SelectProps, MuiSelectProps<string>>;

export const placeholderClassName = 'StyledSelect-placeholder';
export const placeholderDefaultClassName = 'StyledSelect-placeholder-default';

export const StyledSelect: StyledSelectProps = styled(MuiSelect)(
  ({
    theme,
    hasError = false,
    startAdornment,
    fullWidth = true,
  }: SelectProps): CSSObject => ({
    [`&.${inputBaseClasses.root}`]: {
      minWidth: 160,
      ...(fullWidth ? {} : { maxWidth: 320, width: 'fit-content' }),
      backgroundColor: theme.input.background.default,
      boxShadow: theme.typography.inputShadow,
      borderRadius: theme.shape.radius8,
      padding: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'stretch',

      [`& .${inputBaseClasses.input}.Mui-focused`]: {
        outlineWidth: theme.spacing(1),
        outlineColor: alpha(
          hasError ? theme.input.border.errorActive : theme.input.border.active,
          0.15,
        ),
        outlineStyle: 'solid',

        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: hasError
            ? theme.input.border.errorActive
            : theme.input.border.active,
        },
      },
      [`&:not(&.Mui-disabled):hover .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: hasError
          ? theme.input.border.errorActive
          : theme.input.border.active,
        borderWidth: hasError ? 2 : 1,
      },
      [`>svg:not(.${selectClasses.icon}):not(:last-of-type)`]: {
        marginRight: theme.spacing(2),
      },
      svg: {
        flexShrink: 0,
      },

      '&.Mui-disabled': {
        backgroundColor: theme.input.background.disabled,
      },
    },
    [`&.${inputBaseClasses.root}.${inputBaseClasses.focused}`]: {
      outlineWidth: theme.spacing(1),
      outlineColor: alpha(
        hasError ? theme.input.border.errorActive : theme.input.border.active,
        0.15,
      ),
      outlineStyle: 'solid',
    },
    [`& .${inputBaseClasses.input}.${selectClasses.select}`]: {
      padding: theme.spacing(
        0,
        // Added to ensure that chevron is not directly clickable
        '36px',
        0,
        Boolean(startAdornment) ? '36px' : 2,
      ),
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      height: 32,
      display: 'flex',
      alignItems: 'center',
      zIndex: 1,
    },
    [`& .${outlinedInputClasses.notchedOutline}`]: {
      borderColor: hasError
        ? theme.input.border.error
        : theme.input.border.default,
    },
    [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
      borderColor: hasError
        ? theme.input.border.error
        : theme.input.border.active,
    },
    [`&.Mui-disabled .${outlinedInputClasses.notchedOutline}`]: {
      borderColor: theme.input.border.default,
    },
    [`& .${boxClasses.root}`]: {
      width: 32,
      height: 32,
      display: 'flex',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    [`& .${selectClasses.icon}`]: {
      position: 'relative',
      right: 'unset',
      top: 'unset',
      color: theme.icon.secondary,

      '&.Mui-disabled': {
        color: theme.icon.tertiary,
      },
    },
    [`& .${placeholderClassName}`]: {
      WebkitTextFillColor: theme.text.generic.primary,
      color: theme.text.generic.primary,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      ...(fullWidth ? { width: '100%' } : {}),
    },
    [`& .${placeholderClassName}.${placeholderDefaultClassName}`]: {
      WebkitTextFillColor: theme.text.interactive.disabled,
      color: theme.text.interactive.disabled,
    },
    [`&.Mui-disabled .${placeholderClassName}`]: {
      WebkitTextFillColor: theme.text.interactive.disabled,
      color: theme.text.interactive.disabled,
    },
  }),
);

type StyledWrapper = StyledComponent<
  object,
  DetailedHTMLProps<HTMLAttributes<HTMLLabelElement>, HTMLLabelElement>,
  object
>;

export const VisuallyHidden: StyledWrapper = styled('label')(() => ({
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
}));
